<template>
  <div>
    <div v-if="!notifications.length" class="dw-empty">No notifications yet.</div>
    <div v-else class="dw-notif-list">
      <a
        v-for="n in recent"
        :key="n.notification_id"
        :href="n.link || '#'"
        class="dw-notif-item"
        :class="[
          'dw-notif-item--' + typeStyle(n.type),
          { 'dw-notif-item--unread': !n.read_at },
        ]"
        @click.prevent="handleClick(n)"
      >
        <span class="dw-notif-puck">
          <!-- report_complete keeps the stroked document glyph the popup and
               the list page both use, so all three surfaces agree. -->
          <svg v-if="n.type === 'report_complete'" class="dw-notif-puck-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
            <polyline points="8 14 11 17 16 12" />
          </svg>
          <template v-else>
            <span v-if="isSvgIcon(iconFor(n))" class="ni-svg" v-html="customSvgHtml(iconFor(n))" aria-hidden="true"></span>
            <i v-else class="fa" :class="iconFor(n)" aria-hidden="true"></i>
          </template>
        </span>
        <span class="dw-notif-body">
          <span class="dw-notif-row-top">
            <span class="dw-notif-heading">{{ n.title }}</span>
            <span class="dw-notif-time">{{ relativeTime(n.created_at) }}</span>
          </span>
          <!-- body_preview is html so support_comment rows can bold the
               commenter's name (FI-1642); every server path that builds it
               escapes user content first. same rendering as the popup. -->
          <span v-if="n.body_preview" class="dw-notif-preview" v-html="n.body_preview"></span>
        </span>
      </a>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { record_notification_action as cm_record_notification_action } from '../../networking/apis'
import { tbn_record_notification_action } from '../../networking/tbn_apis'
import { iconForNotification, isSvgIcon, customSvgHtml } from '../../utils/notification_icons'

const MAX_ROWS = 5

export default {
  name: 'widget_notifications',
  props: {
    tenant: { type: String, required: true },
    isTbn: { type: Boolean, default: false },
  },
  computed: {
    ...mapState({
      notifications: state => state.notifications.notifications,
    }),
    recent() {
      return this.notifications.slice(0, MAX_ROWS)
    },
  },
  methods: {
    // same registry + type->variant mapping the navbar popup and the
    // notifications list page use, so a given notification draws the same
    // icon and the same puck color on all three surfaces.
    iconFor(n) {
      return iconForNotification(n)
    },
    isSvgIcon,
    customSvgHtml,
    typeStyle(type) {
      if (type === 'announcement') return 'announcement'
      if (type === 'report_complete') return 'report'
      if (type === 'service_alert') return 'service-alert'
      if (type === 'service_resolved') return 'service-resolved'
      if (type === 'support_comment') return 'support'
      return 'generic'
    },
    relativeTime(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return ''
      const diff = Math.max(0, Date.now() - d.getTime()) / 1000
      if (diff < 60) return 'just now'
      if (diff < 3600) return Math.floor(diff / 60) + 'm ago'
      if (diff < 86400) return Math.floor(diff / 3600) + 'h ago'
      if (diff < 604800) return Math.floor(diff / 86400) + 'd ago'
      return d.toLocaleDateString()
    },
    async handleClick(n) {
      if (!n || !n.notification_id) return
      this.$store.commit('notifications/apply_action_local', n.notification_id)
      try {
        const fn = this.isTbn ? tbn_record_notification_action : cm_record_notification_action
        await fn(this.tenant, n.notification_id)
      } catch (e) { /* best effort; next poll reconciles */ }
      const dest = (n.link || '').trim()
      if (dest && dest !== '/') {
        try {
          if (dest.startsWith('http')) {
            window.location.href = dest
          } else {
            this.$router.push(dest)
          }
        } catch (e) { /* router may reject if already on this route */ }
      }
    },
  },
}
</script>
